import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { appInsights, logException } from '../../services/appInsights';
import GeneralInformation from './SeniorPlanning2Components/GeneralInformation';
import GeneralInformationV2 from './SeniorPlanning2Components/GeneralInformationV2';
import SpiritualInformation from './SeniorPlanning2Components/SpiritualInformation';
import HealthInsurancePlan from './SeniorPlanning2Components/HealthInsurancePlan';
import ExpandingList from '../ExpandingList/ExpandingList';
import TransportationPlan from './SeniorPlanning2Components/TransportationPlan';
import HousingPlan from './SeniorPlanning2Components/HousingPlan';
import EmploymentPlan from './SeniorPlanning2Components/EmploymentPlan';
import FallPlan from './SeniorPlanning2Components/FallPlan';
import BackupPlan from './SeniorPlanning2Components/BackupPlan';
import Scoring from './SeniorPlanning2Components/Scoring';
import { callApi } from '../../util/apiWrapper';
import { dateTimeToS, dateTimeUtcToS, getCurrentDateTime } from '../../util/dateUtil';
import { createPrintedList, titleize } from '../../util/stringUtil';
import { btNavyBlueColor, whiteColor } from '../../assets/jss/nextjs-material-kit-pro';
import RequiredInfoDialog from '../Shared/RequiredInfoDialog';
import ConfirmSubmitDialog from './SeniorPlanning2Dialogs/ConfirmSubmitDialog';
import CloseIcon from '@material-ui/icons/Close';
import PleaseWait from '../Shared/PleaseWait';
import useAuthRedirect from '../../hooks/authRedirectHook';
import MyFuturePlan from './SeniorPlanning2Components/MyFuturePlan';
import { isValidPhoneNumber, validateBackupPlanAndTasks, validatePlanSection } from '../../util/planValidation';
import { validateDispositionFields } from '../../utils/scoring';
import { PLANNING_MEETING_FORM_VERSION } from '../../constants/general';
import { displayStatus, isAdmin, isAdminOrFHT, isAlumni, isFHT, isStudent } from '../../util/miscUtil';
import { resolvePlanningFormVersion } from '../../util/formVersion';
import { trackApiRequestAudit } from '../../util/auditHelper';
import MeetingDiscussionDialog from '../PlanningMeetingDialogs/MeetingDiscussionDialog';
import InfoIcon from '@material-ui/icons/Info';
import { logAudit } from '../../util/auditUtil';
import { METHOD } from '../../constants/method';

const formStyles = makeStyles((theme) => ({
  subTitle: {
    margin: '15px 0',
    fontSize: '1.05rem',
    fontWeight: 500,
  },
  firstSectionSpacer: {
    height: 40,
  },
  sectionSpacer: {
    height: 60,
    padding: '10px 0',
  },
  rowSpacer: {
    height: 30,
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  textArea5RowSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 85,
    },
    [theme.breakpoints.up('sm')]: {
      height: 75,
    },
  },
  textArea10RowSpacer: {
    height: 185,
  },
  textRowLabelSpacer: {
    [theme.breakpoints.down('md')]: {
      height: 90,
      paddingTop: 5,
      overflow: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      height: 50,
      paddingTop: 8,
      overflow: 'auto',
    },
  },
  dialogHeaderFooter: {
    backgroundColor: btNavyBlueColor,
    color: whiteColor,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textRowLargeLabelSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 60,
      paddingTop: 5,
    },
    [theme.breakpoints.up('sm')]: {
      height: 50,
      paddingTop: 8,
    },
  },
  buttonWidth: {
    width: 110,
  },
  buttonSpacer: {
    height: 50,
    marginTop: 20,
  },
  meetingDiscussionButton: {
    width: '140px',
  },
  firstSectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
    },
  },
  sectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  sectionSubTitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.80rem',
      fontWeight: 600,
      margin: '2px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.90rem',
      fontWeight: 600,
      margin: '5px 0',
    },
  },
  meetingDiscussionTitle: {
    color: btNavyBlueColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.20rem',
      fontWeight: 700,
      margin: '15px 5px',
    },
  },
  meetingDiscussionNotes: {
    color: btNavyBlueColor,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.00rem',
      margin: '15px 5px',
    },
  },
  disabledSectionTitle: {
    color: 'gray',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  hideForXs: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  hideForMd: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
  },
  datepicker: {
    width: '230px',
    '& .MuiFilledInput-root': {
      background: 'rgb(255, 255, 255)',
    },
  },
  datePickerRowSpacer: {
    height: 50,
    paddingTop: 8,
    marginBottom: 10,
  },
  reminder: {
    color: 'blue',
    fontSize: '1.05rem',
    margin: '10px 0',
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  bigLabel: {
    fontSize: '0.85rem',
  },
  addButton: {
    padding: 0,
    color: btNavyBlueColor,
  },
  topMargin10: {
    marginTop: 10,
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  commentSaveNote: {
    fontSize: '0.90rem',
    fontWeight: 600,
    color: 'blue',
  },
  meetingSaveNote: {
    fontSize: '0.90rem',
    fontWeight: 600,
    color: 'blue',
  },
  floatingSaveButton: {
    width: 210,
    position: 'absolute',
    right: 15,
    bottom: 15,
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  floatingSubmitButton: {
    width: 210,
    position: 'absolute',
    left: 15,
    bottom: 15,
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  note: {
    color: 'blue',
  },
  warning: {
    backgroundColor: '#fdb515',
  },
  space10After: {
    marginBottom: 10,
  },
  totalScore: {
    color: btNavyBlueColor,
    fontSize: '1.00rem',
    fontWeight: 600,
    paddingBottom: 25,
  },
  tasksHeader: {
    fontSize: '1.05rem',
    fontWeight: 600,
    margin: '10px, 0, 5px 0',
  },
  automaticTaskHeading: {
    fontSize: '1.00rem',
    fontWeight: 500,
    margin: '5px 0',
  },
  automaticTasksList: {
    height: 23,
    margin: '2px 0',
  },
  printedTasksList: {
    height: 25,
  },
  errorAlert: {
    backgroundColor: '#fa0000',
    width: '900px',
  },
  submittedTS: {
    fontStyle: 'italic',
    fontSize: '1.00rem',
    fontWeight: 600,
    color: btNavyBlueColor,
  },
  discussionTooltip: {
    paddingTop: '15px',
    width: 530,
    maxWidth: 530,
  },
}));

const NAME = 'Meeting#2';
const SeniorPlanning2 = inject(
  'SeniorStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.SeniorStore;
    const auth = props.AuthStore;

    const planningMeeting2FormVersion = resolvePlanningFormVersion(undefined, store?.planform2Obj?.version, PLANNING_MEETING_FORM_VERSION);
    const commentRef = useRef();
    const meetingCommentRef = useRef();

    const { handleSaveReturn } = props;

    const formClasses = formStyles();

    // prettier-ignore
    const [form, setForm] = useState({
      spiritualPlan: '',
      church: '',
      healthInsPlan: '',
      healthInsCover: '',
      primaryDoctor: '',
      dentist: '',
      therapist: '',
      support: [{ name: '' }, { name: '' }, { name: '' }],
      activities: [{ name: '' }, { name: '' }, { name: '' }],
      driversLicense: '',
      learnersPermit: '',
      completedDriversEd: '',
      enrolledDriversEd: '',
      completeDriversEdDate: '',
      vehicle: '',
      vehicleCostPerMonth: 0,
      vehiclePaid: '',
      transportationPlans: '',
      autoInsurance: '',
      autoInsurancePaid: '',
      autoInsuranceCoverage: '',
      autoInsuranceCostPerMonth: '',
      usePublicTransportation: '',
      publicTransportation: '',
      publicTransportationCost: 0,
      publicTransportationPaid: '',
      summerHousingPlan: '',
      summerHousingPlanCost: 0,
      summerHousingExpectations: '',
      summerEmployWork: '',
      summerEmployApply: '',
      summerEmployHours: 0,
      fallPlan: '',
      fallEmploymentPlace: '',
      fallLivingArrangements: '',
      fallTransportationRequired: '',
      fallCCTradeNameLocation: '',
      fallCCTradeAccepted: '',
      fallTradeOrMajor: '',
      fallCCTradeStartDate: '',
      fallCCTradeCareerGoal: '',
      fallCCTradeLivingArrangements: '',
      fallCCTradeTransportationRequirements: '',
      fallCCTradePaymentPlan: '',
      fall4YearNameLocation: '',
      fall4YearMajor: '',
      fall4YearStartDate: '',
      fall4YearCareerGoal: '',
      fall4YearLivingArrangements: '',
      fall4YearTransportationRequirements: '',
      fall4YearFafsaDate: '',
      fall4YearCostTuition: 0,
      fall4YearCostBooks: 0,
      fall4YearCostHousing: 0,
      fall4YearCostOther: 0,
      fall4YearCostMealPlan: 0,
      fall4YearFamilyAssistance: '',
      fallMilitaryBranch: '',
      fallMilitaryMepsDate: '',
      fallMilitaryBasicTrainingDate: '',
      fallMilitaryRecruiter: '',
      fallJobCorpsLocation: '',
      fallJobCorpsProgram: '',
      fallJobCorpsStartDate: '',
      fallJobCorpsTransportation: '',
      backupPlan: '',
      backupPlanPreparations: '',
      backupPlanLiving: '',
      backupPlanCost: '',
      backupPlanNeed: '',
      backupPlanHelp: '',
      tasks: [
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
        { name: '' },
      ],
      scholarships: [{ name: '' }, { name: '' }, { name: '' }],
      loans: [{ name: '' }, { name: '' }, { name: '' }],
      scholarshipReceivedInitial: '',
      understandWhatINeedToDoInitial: '',
      comments: [],
      fall4YearAccepted: '',
      fallCCTradeScholarships: [{ name: '' }, { name: '' }, { name: '' }],
      fallCCTradeLoans: [{ name: '' }, { name: '' }, { name: '' }],
      fallCCTradeFamilyAssistance: '',
      meetingComments: [],
      status: '',
      statusTimestamp: null,
      dispositionReason: '',
      followup: false,
      gradingSheet2Completed: -1,
      gradingSheet2Submitted: -1,
      gradingSheet2Clarity: -1,
      module4Submitted: -1,
      module4Completed: -1,
      module5Submitted: -1,
      module5Completed: -1,
      module6Submitted: -1,
      module6Completed: -1,
      fallEmploymentFullPartTime: '',
      fallMilitaryMepsDateDoNotKnow: false,
      fallMilitaryBasicTrainingDateDoNotKnow: false,
      fallJobCorpsStartDateDoNotKnow: false,
      fallMilitaryMepsDateDoNotKnowReason: '',
      fallMilitaryBasicTrainingDateDoNotKnowReason: '',
      fallJobCorpsStartDateDoNotKnowReason: '',
      automaticTasks: [],
      rubricMotivation: 0,
      rubricMotivationExplain: '',
      rubricFuture: 0,
      rubricFutureExplain: '',
      rubricSFTeam: 0,
      rubricSFTeamExplain: '',
      rubricMotivationScore: 0,
      rubricSuccess: 0,
      version: planningMeeting2FormVersion,
    });

    const buildPlanningForm2Item = (planningFormData, userId = auth.user.result.id) => ({
      student_id: store.senior.id,
      created_at: new Date(),
      created_by: userId,
      updated_at: new Date(),
      updated_by: userId,
      planning_form_2_data: planningFormData,
    });

    const [userList, setUserList] = useState([]);
    const [discussionNotes, setDiscussionNotes] = useState('');
    const [originalDiscussionNotes, setOriginalDiscussionNotes] = useState('');
    const [discussionNoteUpdates, setDiscussionNoteUpdates] = useState([]);

    const auditedPlanningForm2Request = async ({ item, action, requestMethod }) => {
      try {
        await trackApiRequestAudit({
          auth,
          payload: { item },
          action,
          auditType: NAME,
          requestMethod,
        });
      } catch (auditError) {
        logException(auditError);
        console.log('Error auditing Planning Form 2 request:', auditError);
      }
      return callApi(auth, 'planningform2', requestMethod, { item });
    };

    const formRef = useRef(form);
    useEffect(() => {
      formRef.current = form;
    }, [form]);

    const writeSeniorPlan2 = async (closeInd, currentFormData = formRef.current) => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;

      try {
        store.setLoading(false);

        if (originalStatus !== 'reopened' && currentFormData.status === 'reopened') {
          await sendPlanningForm2ReopenedEmail(store.senior.id);
        }

        let updatedPlanningForm2 = null;

        if (store.planform2Obj && store.planform2Obj.id && store.planform2Obj.id > 0) {
          updatedPlanningForm2 = {
            ...store.planform2Obj,
            planning_form_2_data: currentFormData,
            updated_at: new Date(),
            updated_by: auth.user.result.id,
          };
        }

        if (updatedPlanningForm2) {
          await auditedPlanningForm2Request({
            item: updatedPlanningForm2,
            action: 'save',
            requestMethod: 'PUT',
          });
        } else {
          store.setLoading(true);

          const effectiveFormData = {
            ...currentFormData,
            status: currentFormData.status || 'created',
          };

          if (!currentFormData.status) {
            setForm(effectiveFormData);
          }

          const createPlanningForm2Item = buildPlanningForm2Item(effectiveFormData);
          const res = await auditedPlanningForm2Request({
            item: createPlanningForm2Item,
            action: 'save',
            requestMethod: 'POST',
          });

          if (res.result) {
            updatedPlanningForm2 = res.result;
            store.setPlanform2Obj(updatedPlanningForm2);
            setFetchSuccess(true);
          }
        }

        appInsights.trackTrace({ message: `Planning Form 2 saved by ${auth.user.result.email}` });

        store.setLoading(false);
        closeInd && handleSaveReturn();
      } catch (error) {
        logException(error);
        store.setLoading(false);
      } finally {
        isSavingRef.current = false;
      }
    };

    const [addNote, setAddNote] = useState('');
    const [addMeetingNote, setAddMeetingNote] = useState('');

    const [originalStatus, setOriginalStatus] = useState('');

    const [requiredFields, setRequiredFields] = useState(null);
    const [requiredFieldsIntro, setRequiredFieldsIntro] = useState('');
    const [showRequiredFieldsDialog, setShowRequiredFieldsDialog] = useState(false);
    const [continueSubmit, setContinueSubmit] = useState(false);
    const [showConfirmSubmitDialog, setShowConfirmSubmitDialog] = useState(false);

    const [showMeetingDiscussionDialog, setShowMeetingDiscussionDialog] = useState(false);
    const [meetingDiscussionItemSubmittedAttempted, setMeetingDiscussionItemSubmittedAttempted] = useState(false);

    const [warning, setWarning] = useState('');
    const [showWarning, setShowWarning] = useState(false);

    const [showIncomplete, setShowIncomplete] = useState(false);

    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);

    const [fetchSuccess, setFetchSuccess] = useState(false);
    const isSavingRef = useRef(false);

    const handleClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setShowWarning(false);
    };

    const handleErrorClose = (event, reason) => {
      if (reason === 'clickaway') {
        return;
      }
      setShowError(false);
    };

    const action = (
      <React.Fragment>
        <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </React.Fragment>
    );

    const errorAction = (
      <React.Fragment>
        <IconButton size='small' aria-label='close' color='inherit' onClick={handleErrorClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </React.Fragment>
    );

    const handleConfirmSubmitDialogClose = () => {
      setShowConfirmSubmitDialog(false);
    };

    const handleHaveVehicle = (haveVehicle) => {
      if (haveVehicle === 'yes') {
        setForm({
          ...form,
          vehicle: haveVehicle,
          usePublicTransportation: '',
          publicTransportation: '',
          publicTransportationCost: '',
          publicTransportationPaid: '',
          transportationPlans: '',
        });
      } else {
        setForm({
          ...form,
          vehicle: haveVehicle,
          vehicleCostPerMonth: '',
          vehiclePaid: '',
          autoInsurance: '',
          autoInsuranceCoverage: '',
          autoInsuranceCostPerMonth: '',
        });
      }
    };

    const handleContinueSubmit = (response) => {
      if (response) {
        setContinueSubmit(response);
        setShowConfirmSubmitDialog(false);
      }
    };

    useAuthRedirect(auth);

    useEffect(() => {
      window.scrollTo({ top: 0 });

      const fetchPlanningForm2 = async (studentId) => {
        let planningFormId;
        try {
          store.setLoading(true);
          store.setSearching(true);

          const planningform2 = await callApi(auth, 'planningform2/' + studentId, 'GET');

          if (planningform2 && planningform2.result && planningform2.result.id > 0) {
            store.setPlanform2Obj(planningform2.result);
            planningFormId = planningform2.result.id;
            setFetchSuccess(true);

            let adjustArraysForm;

            try {
              adjustArraysForm =
                typeof planningform2.result.planning_form_2_data === 'string'
                  ? JSON.parse(planningform2.result.planning_form_2_data)
                  : planningform2.result.planning_form_2_data || {};
            } catch (parseErr) {
              console.error('Failed to parse planning_form_2_data JSON:', parseErr);
              appInsights.trackTrace({ message: `Failed to parse planning_form_2_data JSON: ${parseErr}` });
              adjustArraysForm = {};
            }

            if (!adjustArraysForm || typeof adjustArraysForm !== 'object') {
              adjustArraysForm = {};
            }

            adjustArraysForm.version = resolvePlanningFormVersion(
              adjustArraysForm.version,
              planningform2.result.version,
              planningMeeting2FormVersion
            );

            if (!adjustArraysForm.hasOwnProperty('support')) {
              adjustArraysForm.support = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('activities')) {
              adjustArraysForm.activities = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('fallCCTradeScholarships')) {
              adjustArraysForm.fallCCTradeScholarships = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('fallCCTradeLoans')) {
              adjustArraysForm.fallCCTradeLoans = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('scholarships')) {
              adjustArraysForm.scholarships = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('loans')) {
              adjustArraysForm.loans = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('tasks')) {
              adjustArraysForm.tasks = [{ name: '' }, { name: '' }, { name: '' }];
            }
            if (!adjustArraysForm.hasOwnProperty('meetingComments')) {
              adjustArraysForm.meetingComments = [];
            }
            if (!adjustArraysForm.hasOwnProperty('automaticTasks')) {
              adjustArraysForm.automaticTasks = [];
            }

            if (adjustArraysForm.version === PLANNING_MEETING_FORM_VERSION) {
              if (!adjustArraysForm.hasOwnProperty('myFuturePlanA')) {
                adjustArraysForm.myFuturePlanA = '';
              }
              if (!adjustArraysForm.hasOwnProperty('myFuturePlanB')) {
                adjustArraysForm.myFuturePlanB = '';
              }
              if (!adjustArraysForm.hasOwnProperty('gapPeriodPlan')) {
                adjustArraysForm.gapPeriodPlan = '';
              }
            }

            if (!adjustArraysForm.hasOwnProperty('rubricMotivation')) {
              adjustArraysForm.rubricMotivation = 0;
            }
            if (!adjustArraysForm.hasOwnProperty('rubricFuture')) {
              adjustArraysForm.rubricFuture = 0;
            }
            if (!adjustArraysForm.hasOwnProperty('rubricSFTeam')) {
              adjustArraysForm.rubricSFTeam = 0;
            }
            if (!adjustArraysForm.hasOwnProperty('rubricMotivationScore')) {
              adjustArraysForm.rubricMotivationScore = 0;
            } else {
              adjustArraysForm.rubricMotivationScore =
                (adjustArraysForm.rubricMotivation + adjustArraysForm.rubricFuture + adjustArraysForm.rubricSFTeam) / 3;
            }
            if (!adjustArraysForm.hasOwnProperty('rubricSuccess')) {
              adjustArraysForm.rubricSuccess = 0;
            }

            adjustArraysForm.version = resolvePlanningFormVersion(
              adjustArraysForm.version,
              planningform2.result?.version,
              PLANNING_MEETING_FORM_VERSION
            );

            setOriginalStatus(adjustArraysForm.status);
            setForm(adjustArraysForm);
          } else {
            const createdFormData = {
              ...form,
              status: 'created',
              version: planningMeeting2FormVersion,
            };
            setForm(createdFormData);
            setOriginalStatus('created');
            setFetchSuccess(true);

            try {
              const createPlanningForm2Item = buildPlanningForm2Item(null);
              const createResult = await auditedPlanningForm2Request({
                item: createPlanningForm2Item,
                action: 'save',
                requestMethod: METHOD.POST,
              });

              if (createResult && createResult.result) {
                const createdRecord = createResult.result;
                const persistedFormData = {
                  ...createdFormData,
                  status: createdFormData.status || 'created',
                  version: resolvePlanningFormVersion(createdFormData.version, createdRecord.version, planningMeeting2FormVersion),
                };

                const updatedPlanningForm2 = {
                  ...createdRecord,
                  planning_form_2_data: persistedFormData,
                  updated_at: new Date(),
                  updated_by: auth.user.result.id,
                };

                const putResult = await auditedPlanningForm2Request({
                  item: updatedPlanningForm2,
                  action: 'save',
                  requestMethod: METHOD.PUT,
                });

                store.setPlanform2Obj(putResult?.result || updatedPlanningForm2);
                planningFormId = putResult?.result?.id || createdRecord.id;
                setForm(persistedFormData);
                setOriginalStatus(persistedFormData.status || 'created');
              }
            } catch (createError) {
              logException(createError);
              console.log('Error creating Planning Form 2 new record:', createError);
              appInsights.trackTrace({ message: `Error creating Planning Form 2 new record: ${createError}` });
            }
          }
          store.setSearching(false);
          store.setLoading(false);
          return planningFormId;
        } catch (error) {
          if (error?.status === 404 || error?.response?.status === 404 || error?.message?.includes('404')) {
            setFetchSuccess(true);
          } else {
            setFetchSuccess(false);
            setError(
              'Your Planning Form 2 did not load successfully. Please close your browser and retry. Failure to do so may result in data loss!'
            );
            appInsights.trackTrace({ message: 'Planning Form 2 data failed to load. Error displayed to user.' });
            setShowError(true);
            logException(error);
          }
          store.setSearching(false);
          store.setLoading(false);
        }
      };

      const fetchPlanningForm2DiscussionNotes = async (studentId) => {
        const fetchedDiscussion = await callApi(auth, 'planningform2/discussion/student/' + studentId, 'GET');
        if (fetchedDiscussion && fetchedDiscussion.result && fetchedDiscussion.result.discussion_notes.length > 0) {
          setDiscussionNotes(fetchedDiscussion.result.discussion_notes);
        }
      };

      const fetchUserList = async () => {
        const userList = await callApi(auth, 'user/list', 'GET');
        if (userList && userList.result && userList.result.length > 0) {
          setUserList(userList.result);
        }
      };

      const fetchPlanningForm2DiscussionUpdates = async (planningFormId) => {
        if (!planningFormId) return;
        try {
          const discussionUpdates = await callApi(auth, 'planningform2/discussion/form/' + planningFormId, 'GET');
          if (discussionUpdates && discussionUpdates.results && discussionUpdates.results.length > 0) {
            setDiscussionNoteUpdates(discussionUpdates.results);
          }
        } catch (discussionUpdatesError) {
          logException(discussionUpdatesError);
          console.log('Error fetching Planning Form 2 discussion updates:', discussionUpdatesError);
        }
      };

      if (store.senior.id > 0) {
        fetchUserList();
        fetchPlanningForm2DiscussionNotes(store.senior.id);
        // Wait for the resolved planning form id to avoid reading a stale/unset store value
        fetchPlanningForm2(store.senior.id).then((planningFormId) => fetchPlanningForm2DiscussionUpdates(planningFormId));
      }
    }, [auth, store, store.senior]);

    // AUTO-SAVE
    useEffect(() => {
      const canEdit = !isEditDisabled();
      if (canEdit) {
        const interval = setInterval(() => {
          if (fetchSuccess) {
            handleSavePlanningForm2(false, formRef.current);
            appInsights.trackTrace({ message: `Auto saved Planning Form 2 for ${auth.user.result.email}` });
          } else {
            appInsights.trackTrace({ message: 'Planning Form 2 data failed to load. Error displayed to user.' });
            setError(
              'Your Planning Form 2 cannot be saved, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
            );
            appInsights.trackTrace({ message: 'Planning Form 2 data failed to load. Error displayed to user on auto-save.' });
            setShowError(true);
          }
        }, 300000);
        return () => clearInterval(interval);
      }
    }, [auth, fetchSuccess, form.status]);

    const isEditDisabled = () => {
      if (auth.roles && (isStudent(auth.roles) || isAlumni(auth.roles))) {
        if (form.status === '' || form.status === 'created' || form.status === 'reopened' || form.status === 'incomplete') {
          return false;
        }
      }
      return true;
    };

    const showDisposition = () => {
      let rc = false;
      if (isAdmin(auth.roles)) {
        if (originalStatus === 'submitted' || originalStatus === 'incomplete' || form.status === 'incomplete') {
          rc = true;
        }
      }
      return rc;
    };

    const handleAddMeetingDiscussionItem = async () => {
      try {
        store.setLoading(false);

        const item = {
          planning_meeting_2_id: store.planform2Obj.id,
          student_id: store.senior.id,
          discussion_notes: discussionNotes,
          created_at: getCurrentDateTime(),
          created_by: auth.user.result.id,
          modified_at: getCurrentDateTime(),
          modified_by: auth.user.result.id,
        };

        const newNote = await callApi(auth, 'planningform2/discussion', 'POST', { item: item });
        if (newNote && newNote.result) {
          setDiscussionNoteUpdates((prev) => [newNote.result, ...(Array.isArray(prev) ? prev : [])]);
        }

        const auditObj = {
          audit_ts: new Date(),
          user_id: auth.user.result.id,
          username: auth.user.result.first_name + ' ' + auth.user.result.last_name,
          audit_type: 'access request',
          action: 'discussion update',
          value: JSON.stringify(item).length > 999 ? JSON.stringify(item).substring(0, 999) : JSON.stringify(item),
        };
        logAudit(auth, auditObj);

        appInsights.trackTrace({ message: `PlanningForm2 Discussion item submitted by ${auth.user.result.email}` });

        store.setLoading(false);
      } catch (error) {
        logException(error);
        setDiscussionNotes(originalDiscussionNotes);
        store.setLoading(false);
        setError('Failed to submit Planning Form 2 Discussion item. Please try again or check your network connection.');
        setShowError(true);
        console.log('Error submitting Planning Form 2 Discussion item:', error);
      } finally {
        // handleMeetingDiscussionClose();
        setShowMeetingDiscussionDialog(false);
      }
    };

    const handleAddComment = () => {
      const updatedComments = [
        ...form.comments,
        {
          timestamp: getCurrentDateTime(),
          text: addNote,
          user: auth.user.result.first_name + ' ' + auth.user.result.last_name,
        },
      ];
      setForm({ ...form, comments: updatedComments });
      setAddNote('');
    };

    const handleAddMeetingComment = () => {
      const updatedMeetingComments = [
        ...form.meetingComments,
        {
          timestamp: getCurrentDateTime(),
          text: addMeetingNote,
          user: auth.user.result.first_name + ' ' + auth.user.result.last_name,
        },
      ];
      setForm({ ...form, meetingComments: updatedMeetingComments });
      setAddMeetingNote('');
    };

    const shouldDisplayStatusNotes = () => {
      return !!(form.dispositionReason && form.dispositionReason.length > 0);
    };

    const displayUserName = (userId) => {
      const user = userList.find((u) => u.id === parseInt(userId, 10));
      return user ? `${user.first_name} ${user.last_name}` : 'N/A';
    };

    const formatDiscussionUpdateData = (data) => {
      if (!data || !Array.isArray(data)) return '';
      return (
        <>
          <div style={{ fontWeight: 'bold', margin: '10px 0 20px 0', fontSize: '1.2rem' }}>Change History</div>
          {data.map((item, index) => (
            <div key={index}>
              <ul style={{ margin: '0 0 15px 0', fontSize: '0.95rem' }}>
                <li>
                  <strong>Updated by</strong> {displayUserName(item.modified_by)} <strong>at</strong> {dateTimeUtcToS(item.modified_at) || 'N/A'}
                </li>
              </ul>
            </div>
          ))}
        </>
      );
    };

    const markFormIncomplete = (incomplete) => {
      setShowIncomplete(!showIncomplete);
      if (incomplete) {
        setForm({ ...form, status: 'incomplete', statusTimestamp: new Date() });
      }
    };

    const canDisposition = () => {
      const { rc, requiredFields } = validateDispositionFields(form, showIncomplete);
      setRequiredFieldsIntro('Please make these adjustments before continuing.');
      setRequiredFields(requiredFields);
      return rc;
    };

    const sendPlanningForm2ReopenedEmail = async (id) => {
      try {
        store.setLoading(true);
        await callApi(auth, 'planningform2reopenemail', 'POST', { student_id: id });
        appInsights.trackTrace({ message: `PlanningForm2 reopened email sent to ${auth.user.result.email}` });
        store.setLoading(false);
      } catch (error) {
        logException(error);
        store.setLoading(false);
        console.log('Error sending Planning Form 2 reopened email:', error);
      }
    };

    const handleSavePlanningForm2 = (closeInd, currentFormData = formRef.current) => {
      if (!fetchSuccess) {
        setError(
          'Your Planning Form 2 cannot be saved, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
        );
        setShowError(true);
        appInsights.trackTrace({ message: 'Planning Form 2 data failed to load. Error displayed to user on save.' });
        return;
      }

      if (isAdmin(auth.roles) && canDisposition() === false) {
        setShowRequiredFieldsDialog(true);
        return;
      }

      if (isAdmin(auth.roles) && currentFormData.status === 'reopened') {
        if (!currentFormData.dispositionReason || currentFormData.dispositionReason.length === 0) {
          setWarning('You must provide a reason for returning the planning form.');
          setShowWarning(true);
          return;
        }
      }

      writeSeniorPlan2(closeInd, currentFormData);
    };

    useEffect(() => {
      const canSubmit = () => {
        let rc = true;
        let requiredFields = [];

        if (!form.healthInsPlan || form.healthInsPlan.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please select the type of health insurance plan you will have after graduation.' }];
          rc = false;
        }

        if (form.healthInsPlan !== 'None') {
          if (!form.healthInsCover || form.healthInsCover.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please describe the coverage your health insurance plan will provide.' }];
            rc = false;
          }
        }

        if (!form.primaryDoctor || form.primaryDoctor.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter the name of your primary doctor.' }];
          rc = false;
        }
        if (!form.primaryDoctorPhone || form.primaryDoctorPhone.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter your primary doctor phone number.' }];
          rc = false;
        } else if (!isValidPhoneNumber(form.primaryDoctorPhone)) {
          requiredFields = [...requiredFields, { requirement: 'Please enter a valid phone number for primary doctor phone number (10 digits).' }];
          rc = false;
        }

        if (!form.dentist || form.dentist.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter the name of your dentist.' }];
          rc = false;
        }
        if (!form.dentistPhone || form.dentistPhone.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter your dentist phone number.' }];
          rc = false;
        } else if (!isValidPhoneNumber(form.dentistPhone)) {
          requiredFields = [...requiredFields, { requirement: 'Please enter a valid of your dentist phone number (10 digits).' }];
          rc = false;
        }

        if (!form.therapist || form.therapist.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter the name of your mental health provider.' }];
          rc = false;
        }
        if (!form.therapistPhone || form.therapistPhone.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please enter your mental health provider phone number.' }];
          rc = false;
        } else if (!isValidPhoneNumber(form.therapistPhone)) {
          requiredFields = [...requiredFields, { requirement: 'Please enter a valid phone number for your mental health provider (10 digits).' }];
          rc = false;
        }

        if (!form.version || form.version !== PLANNING_MEETING_FORM_VERSION) {
          let peopleInList = 0;
          form.support.forEach((t) => {
            if (t.name.length > 0) {
              peopleInList += 1;
            }
          });
          if (peopleInList < 1) {
            requiredFields = [
              ...requiredFields,
              {
                requirement: 'Please enter the name of at least one individual you can contact for guidance and support.',
              },
            ];
            rc = false;
          }

          let activitiesInList = 0;
          form.activities.forEach((t) => {
            if (t.name.length > 0) {
              activitiesInList += 1;
            }
          });
          if (activitiesInList < 1) {
            requiredFields = [
              ...requiredFields,
              {
                requirement:
                  'Please enter the names of at least one activity you participate in and will continue to participate in after graduation.',
              },
            ];
            rc = false;
          }
        }

        if (!form.driversLicense || form.driversLicense.length === 0) {
          requiredFields = [...requiredFields, { requirement: "Please indicate whether you have a driver's license." }];
          rc = false;
        }

        if (form.driversLicense === 'no') {
          if (!form.version || form.version !== PLANNING_MEETING_FORM_VERSION) {
            if (!form.completedDriversEd || form.completedDriversEd.length === 0) {
              requiredFields = [...requiredFields, { requirement: "Please indicate whether you have completed a driver's education course." }];
              rc = false;
            }
            if (form.completedDriversEd === 'no') {
              if (!form.learnersPermit || form.learnersPermit.length === 0) {
                requiredFields = [...requiredFields, { requirement: "Please indicate whether you have a learner's permit." }];
                rc = false;
              }
              if (!form.enrolledDriversEd || form.enrolledDriversEd.length === 0) {
                requiredFields = [...requiredFields, { requirement: "Please indicate whether you are enrolled in a driver's education course." }];
                rc = false;
              }
            }
          }
        }

        if (!form.vehicle || form.vehicle.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please indicate whether you will have a vehicle.' }];
          rc = false;
        }

        if (form.vehicle === 'yes') {
          if (!form.vehicleCostPerMonth || form.vehicleCostPerMonth.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate the monthly cost of the vehicle.' }];
            rc = false;
          }
          if (!form.vehiclePaid || form.vehiclePaid.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate who will pay for the vehicle.' }];
            rc = false;
          }
          if (!form.autoInsurance || form.autoInsurance.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate whether you will have auto insurance.' }];
            rc = false;
          }
        }

        if (form.autoInsurance === 'yes') {
          if (!form.autoInsuranceCoverage || form.autoInsuranceCoverage.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please explain the auto insurance coverage.' }];
            rc = false;
          }
          if (!form.autoInsuranceCostPerMonth || form.autoInsuranceCostPerMonth.length === 0 || Number(form.autoInsuranceCostPerMonth) === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate the cost per month for auto insurance.' }];
            rc = false;
          }
          if (!form.autoInsurancePaid || form.autoInsurancePaid.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate who will pay for auto insurance.' }];
            rc = false;
          }
        }

        if (form.vehicle === 'no') {
          if (!form.usePublicTransportation || form.usePublicTransportation.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate whether you will be using public transportation.' }];
            rc = false;
          }
          if (form.usePublicTransportation === 'yes') {
            if (!form.publicTransportation || form.publicTransportation.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the type of public transportation you will use.' }];
              rc = false;
            }
            if (!form.publicTransportationCost || form.publicTransportationCost.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please estimate the monthly cost for public transportation.' }];
              rc = false;
            }
            if (!form.publicTransportationPaid || form.publicTransportationPaid.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please explain who will pay for the public transportation.' }];
              rc = false;
            }
          }

          if (form.usePublicTransportation === 'no') {
            if (!form.transportationPlans || form.transportationPlans.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate what you will use for transportation.' }];
              rc = false;
            }
          }
        }

        if (!form.summerHousingPlan || form.summerHousingPlan.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please explain where you will go the day of graduation.' }];
          rc = false;
        }

        if (!form.summerHousingPlanCost || form.summerHousingPlanCost.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please indicate the cost of your housing.' }];
          rc = false;
        }

        if (!form.summerHousingExpectations || form.summerHousingExpectations.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please explain the responsibilities you will have at your housing.' }];
          rc = false;
        }

        if (!form.summerEmployWork || form.summerEmployWork.length === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please explain where you will work.' }];
          rc = false;
        }
        if (!form.summerEmployHours || form.summerEmployHours.length === 0 || Number(form.summerEmployHours) === 0) {
          requiredFields = [...requiredFields, { requirement: 'Please indicate how many hours per week you will be available to work.' }];
          rc = false;
        }

        if (!form.version || form.version !== PLANNING_MEETING_FORM_VERSION) {
          let rcObjBackup = { value: rc };
          validateBackupPlanAndTasks(form, requiredFields, rcObjBackup);
          rc = rcObjBackup.value;
        }
        if (!form.version || form.version !== PLANNING_MEETING_FORM_VERSION) {
          if (!form.fallPlan || form.fallPlan.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please indicate your plan for the fall.' }];
            rc = false;
          }
          if (form.fallPlan === 'Community College/Trade') {
            if (!form.fallCCTradeNameLocation || form.fallCCTradeNameLocation.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the name of the college and/or location.' }];
              rc = false;
            }
            if (!form.fallCCTradeAccepted || form.fallCCTradeAccepted.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate whether you have been accepted at the community college/trade.' }];
              rc = false;
            }
            if (!form.fallTradeOrMajor || form.fallTradeOrMajor.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your trade and/or major.' }];
              rc = false;
            }
            if (!form.fallCCTradeStartDate || form.fallCCTradeStartDate.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your start date for community college/trade.' }];
              rc = false;
            }
            if (!form.fallCCTradeCareerGoal || form.fallCCTradeCareerGoal.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your career goals for community college/trade.' }];
              rc = false;
            }
            if (!form.fallCCTradeFamilyAssistance || form.fallCCTradeFamilyAssistance.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your expected family assistance for community college/trade.' }];
              rc = false;
            }
            if (!form.fallCCTradeLivingArrangements || form.fallCCTradeLivingArrangements.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your living arrangements for fall.' }];
              rc = false;
            }
            if (!form.fallCCTradeTransportationRequirements || form.fallCCTradeTransportationRequirements.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your transportation requirements for fall.' }];
              rc = false;
            }
          }

          if (form.fallPlan === '4-Year College') {
            if (!form.fall4YearNameLocation || form.fall4YearNameLocation.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the name of the college and/or location.' }];
              rc = false;
            }
            if (!form.fall4YearAccepted || form.fall4YearAccepted.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate whether you have been accepted at the community college/trade.' }];
              rc = false;
            }
            if (!form.fall4YearMajor || form.fall4YearMajor.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your trade and/or major.' }];
              rc = false;
            }
            if (!form.fall4YearCareerGoal || form.fall4YearCareerGoal.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your career goals for community college/trade.' }];
              rc = false;
            }
            if (!form.fall4YearStartDate || form.fall4YearStartDate.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your start date for community college/trade.' }];
              rc = false;
            }
            if (!form.fall4YearCostTuition || form.fall4YearCostTuition.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your tuition costs for the fall.' }];
              rc = false;
            }
            if (!form.fall4YearCostBooks || form.fall4YearCostBooks.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your costs for books for the fall.' }];
              rc = false;
            }
            if (!form.fall4YearCostHousing || form.fall4YearCostHousing.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your housing costs for the fall.' }];
              rc = false;
            }
            if (!form.fall4YearCostOther || form.fall4YearCostOther.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your costs for other fees for the fall.' }];
              rc = false;
            }
            if (!form.fall4YearCostMealPlan || form.fall4YearCostMealPlan.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your meal plan costs for the fall.' }];
              rc = false;
            }

            const calculate4YearCosts =
              form.fall4YearCostTuition + form.fall4YearCostBooks + form.fall4YearCostHousing + form.fall4YearCostOther + form.fall4YearCostMealPlan;

            if (calculate4YearCosts === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your expected costs for the fall.' }];
              rc = false;
            }
            if (!form.fall4YearFamilyAssistance || form.fall4YearFamilyAssistance.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your expected family assistance for 4-year college.' }];
              rc = false;
            }
            if (!form.fall4YearLivingArrangements || form.fall4YearLivingArrangements.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your living arrangements requirements for fall.' }];
              rc = false;
            }
            if (!form.fall4YearTransportationRequirements || form.fall4YearTransportationRequirements.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your transportation requirements for fall.' }];
              rc = false;
            }
          }

          if (form.fallPlan === 'Job Corps') {
            if (!form.fallJobCorpsLocation || form.fallJobCorpsLocation.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the Job Corps site location.' }];
              rc = false;
            }
            if (!form.fallJobCorpsProgram || form.fallJobCorpsProgram.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the Job Corps program.' }];
              rc = false;
            }
            if ((!form.fallJobCorpsStartDate || form.fallJobCorpsStartDate.length === 0) && !form.fallJobCorpsStartDateDoNotKnow) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the Job Corps start date or indicate you do not yet know it.' }];
              rc = false;
            }
            if (
              form.fallJobCorpsStartDateDoNotKnow &&
              (!form.fallJobCorpsStartDateDoNotKnowReason || form.fallJobCorpsStartDateDoNotKnowReason.length === 0)
            ) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the reason you do not know your Job Corps start date.' }];
              rc = false;
            }

            if (!form.fallJobCorpsTransportation || form.fallJobCorpsTransportation.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the Job Corps transportation requirements.' }];
              rc = false;
            }
          }

          if (form.fallPlan === 'Military') {
            if (!form.fallMilitaryBranch || form.fallMilitaryBranch.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the military branch.' }];
              rc = false;
            }
            if ((!form.fallMilitaryMepsDate || form.fallMilitaryMepsDate.length === 0) && !form.fallMilitaryMepsDateDoNotKnow) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the MEPS date or indicate you do not yet know it.' }];
              rc = false;
            }
            if (
              form.fallMilitaryMepsDateDoNotKnow &&
              (!form.fallMilitaryMepsDateDoNotKnowReason || form.fallMilitaryMepsDateDoNotKnowReason.length === 0)
            ) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the reason you do not know your MEPS date.' }];
              rc = false;
            }
            if (
              (!form.fallMilitaryBasicTrainingDate || form.fallMilitaryBasicTrainingDate.length === 0) &&
              !form.fallMilitaryBasicTrainingDateDoNotKnow
            ) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the basic training date or indicate you do not yet know it.' }];
              rc = false;
            }
            if (
              form.fallMilitaryBasicTrainingDateDoNotKnow &&
              (!form.fallMilitaryBasicTrainingDateDoNotKnowReason || form.fallMilitaryBasicTrainingDateDoNotKnowReason.length === 0)
            ) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the reason you do not know your basic training date.' }];
              rc = false;
            }
            if (!form.fallMilitaryRecruiter || form.fallMilitaryRecruiter.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate the name of the military recruiter.' }];
              rc = false;
            }
          }

          if (form.fallPlan === 'Continue Employment') {
            if (!form.fallEmploymentPlace || form.fallEmploymentPlace.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your place of employment in the fall.' }];
              rc = false;
            }
            if (!form.fallEmploymentFullPartTime || form.fallEmploymentFullPartTime.length === 0) {
              requiredFields = [
                ...requiredFields,
                { requirement: 'Please indicate whether your employment in the fall will be full-time or part-time.' },
              ];
              rc = false;
            }
            if (!form.fallLivingArrangements || form.fallLivingArrangements.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your fall living arrangements.' }];
              rc = false;
            }
            if (!form.fallTransportationRequired || form.fallTransportationRequired.length === 0) {
              requiredFields = [...requiredFields, { requirement: 'Please indicate your fall transportation requirements.' }];
              rc = false;
            }
          }
        } else if (form.version && form.version === PLANNING_MEETING_FORM_VERSION) {
          let rcObjA = { value: rc };
          validatePlanSection(form, 'A', form.myFuturePlanA, requiredFields, rcObjA);
          rc = rcObjA.value;
          let rcObjB = { value: rc };
          validatePlanSection(form, 'B', form.myFuturePlanB, requiredFields, rcObjB);
          rc = rcObjB.value;

          if (!form.myFuturePlanA || form.myFuturePlanA.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please select a Plan A option.' }];
            rc = false;
          }

          if (!form.myFuturePlanB || form.myFuturePlanB.length === 0) {
            requiredFields = [...requiredFields, { requirement: 'Please select a Plan B option.' }];
            rc = false;
          }
          if (!form.gapPeriodPlan || form.gapPeriodPlan.length === 0) {
            requiredFields = [
              ...requiredFields,
              {
                requirement:
                  'Is there a period of time between your departure or graduation and the start of your Plan A or Plan B? If so, what are your plans for\n' +
                  '              managing that gap?',
              },
            ];
            rc = false;
          }
        }

        setRequiredFieldsIntro(
          'You have not completed all of the required information on your Planning Meeting 2 form. Please make these adjustments before submitting your form.'
        );
        setRequiredFields(requiredFields);
        setContinueSubmit(false);
        return rc;
      };

      if (continueSubmit) {
        if (!fetchSuccess) {
          setError(
            'Your Planning Form 2 cannot be saved, because it was not successfully loaded. Please close your browser and retry. Failure to do so may result in data loss!'
          );
          setShowError(true);
          appInsights.trackTrace({ message: 'Planning Form 2 data failed to load. Error displayed to user on submit.' });
          return;
        }

        if (canSubmit()) {
          async function submitPlanningForm2() {
            try {
              store.setLoading(true);

              const submittedForm = {
                ...form,
                status: 'submitted',
                dispositionReason: '',
                submitted_date: getCurrentDateTime(),
              };

              setForm(submittedForm);

              let updatedPlanningForm2 = null;

              if (store.planform2Obj && store.planform2Obj.id && store.planform2Obj.id > 0) {
                updatedPlanningForm2 = {
                  ...store.planform2Obj,
                  planning_form_2_data: submittedForm,
                  updated_at: new Date(),
                  updated_by: auth.user.result.id,
                };

                await auditedPlanningForm2Request({
                  item: updatedPlanningForm2,
                  action: 'submit',
                  requestMethod: 'PUT',
                });
              } else {
                const createPlanningForm2Item = buildPlanningForm2Item(submittedForm, store.senior.userId);
                const res = await auditedPlanningForm2Request({
                  item: createPlanningForm2Item,
                  action: 'submit',
                  requestMethod: 'POST',
                });

                if (res.result) {
                  updatedPlanningForm2 = res.result;
                  store.setPlanform2Obj(updatedPlanningForm2);
                }
              }

              appInsights.trackTrace({ message: `Planning Form 2 submitted by ${auth.user.result.email}` });
              store.setLoading(false);
              handleSaveReturn();
            } catch (error) {
              logException(error);
              store.setLoading(false);
              console.log('Error submitting updated planning form 2:', error);
            }
          }
          submitPlanningForm2();
        } else {
          setShowRequiredFieldsDialog(true);
        }
      }
    }, [continueSubmit, auth, store, form, fetchSuccess, handleSaveReturn]);

    const handleMeetingDiscussionClose = () => {
      setDiscussionNotes(originalDiscussionNotes);
      setShowMeetingDiscussionDialog(false);
    };

    const editDiscussionNotes = (val) => {
      setOriginalDiscussionNotes(discussionNotes);
      setShowMeetingDiscussionDialog(val);
    };

    const handleRequiredInfoClose = () => {
      setShowRequiredFieldsDialog(false);
    };

    const showSaveButton = () => {
      let rc = false;
      if (isAdmin(auth.roles)) {
        return true;
      }
      if (isStudent(auth.roles) || isAlumni(auth.roles)) {
        if (form.status === 'created' || form.status === 'reopened' || form.status === 'incomplete' || showIncomplete) {
          return true;
        }
      }
      return rc;
    };

    const saveHealthInsuranceTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'Health Insurance Plan - ‘None’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'None') {
        tasks.push(taskName);
      }
      setForm({ ...form, healthInsPlan: val, automaticTasks: tasks });
    };

    const saveDriversLicenseTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'I have a driver’s license - ‘No’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'no') {
        tasks.push(taskName);
      }
      setForm({ ...form, driversLicense: val, automaticTasks: tasks });
    };

    const saveDriversEdTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'I have completed a driver’s education course - ‘No’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'no') {
        tasks.push(taskName);
      }
      setForm({ ...form, completedDriversEd: val, automaticTasks: tasks });
    };

    const saveLearnersPermitTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'I have a learner’s permit - ‘No’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'no') {
        tasks.push(taskName);
      }
      setForm({ ...form, learnersPermit: val, automaticTasks: tasks });
    };

    const saveAutoInsuranceTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'I will have auto insurance - ‘No’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'no') {
        tasks.push(taskName);
      }
      setForm({ ...form, autoInsurance: val, automaticTasks: tasks });
    };

    const saveTalkedToManagerTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'Have I applied or talked to a manager? - ‘No’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === 'no') {
        tasks.push(taskName);
      }
      setForm({ ...form, summerEmployApply: val, automaticTasks: tasks });
    };

    const saveJobCorpsStartDateTask = (val) => {
      const tasks = [...(form.automaticTasks || [])];
      const taskName = 'Job Corps: Start Date = ‘Do Not Know’';
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === true) {
        tasks.push(taskName);
      }
      setForm({ ...form, fallJobCorpsStartDateDoNotKnow: val, automaticTasks: tasks });
    };

    const saveMilitaryMEPSDateTask = (val, planType = '') => {
      const suffix = planType ? planType : '';
      const tasks = [...(form.automaticTasks || [])];
      const taskName = `Military: MEPS Date Date = ‘Do Not Know’${suffix ? ' (' + suffix + ')' : ''}`;
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === true) {
        tasks.push(taskName);
      }
      setForm({
        ...form,
        [`fallMilitaryMepsDateDoNotKnow${suffix}`]: val,
        automaticTasks: tasks,
      });
    };

    const saveMilitaryBasicTrainingDateTask = (val, planType = '') => {
      const suffix = planType ? planType : '';
      const tasks = [...(form.automaticTasks || [])];
      const taskName = `Military: Basic Training Date = ‘Do Not Know’${suffix ? ' (' + suffix + ')' : ''}`;
      const idxToDelete = tasks.findIndex((r) => r === taskName);
      if (idxToDelete > -1) {
        tasks.splice(idxToDelete, 1);
      }
      if (val === true) {
        tasks.push(taskName);
      }
      setForm({
        ...form,
        [`fallMilitaryBasicTrainingDateDoNotKnow${suffix}`]: val,
        automaticTasks: tasks,
      });
    };

    const saveFallPlanAndTasks = (plan) => {
      const tasks = [...(form.automaticTasks || [])];
      if (plan === 'Military') {
        const basicTrainingTask = 'Military: Basic Training Date = ‘Do Not Know’';
        const basicTrainingIdx = tasks.findIndex((r) => r === basicTrainingTask);
        if (basicTrainingIdx > -1) {
          tasks.splice(basicTrainingIdx, 1);
        }
        const mepsTask = 'Military: MEPS Date Date = ‘Do Not Know’';
        const mepsIdx = tasks.findIndex((r) => r === mepsTask);
        if (mepsIdx > -1) {
          tasks.splice(mepsIdx, 1);
        }
        if (form.fallMilitaryBasicTrainingDateDoNotKnow) {
          tasks.push(basicTrainingTask);
        }
        if (form.fallMilitaryMepsDateDoNotKnow) {
          tasks.push(mepsTask);
        }
      } else {
        const basicTrainingTask = 'Military: Basic Training Date = ‘Do Not Know’';
        const basicTrainingIdx = tasks.findIndex((r) => r === basicTrainingTask);
        if (basicTrainingIdx > -1) {
          tasks.splice(basicTrainingIdx, 1);
        }
        const mepsTask = 'Military: MEPS Date Date = ‘Do Not Know’';
        const mepsIdx = tasks.findIndex((r) => r === mepsTask);
        if (mepsIdx > -1) {
          tasks.splice(mepsIdx, 1);
        }
      }
      if (plan === 'Job Corps') {
        const jobCorpsStartDateTask = 'Job Corps: Start Date = ‘Do Not Know’';
        const jobCorpsStartDateIdx = tasks.findIndex((r) => r === jobCorpsStartDateTask);
        if (jobCorpsStartDateIdx > -1) {
          tasks.splice(jobCorpsStartDateIdx, 1);
        }
        if (form.fallJobCorpsStartDateDoNotKnow) {
          tasks.push(jobCorpsStartDateTask);
        }
      } else {
        const jobCorpsStartDateTask = 'Job Corps: Start Date = ‘Do Not Know’';
        const jobCorpsStartDateIdx = tasks.findIndex((r) => r === jobCorpsStartDateTask);
        if (jobCorpsStartDateIdx > -1) {
          tasks.splice(jobCorpsStartDateIdx, 1);
        }
      }
      setForm({ ...form, fallPlan: plan, automaticTasks: tasks });
    };

    return (
      <React.Fragment>
        <PleaseWait isLoading={store.loading} />
        {showMeetingDiscussionDialog && (
          <Dialog
            open={showMeetingDiscussionDialog}
            onClose={handleMeetingDiscussionClose}
            scroll='paper'
            fullWidth
            maxWidth='md'
            aria-labelledby='Meeting Discussion Item Dialog'
          >
            <DialogTitle id='basic-info-dialog' className={formClasses.dialogHeaderFooter}>
              Planning Meeting 2 Discussion Notes
            </DialogTitle>
            <DialogContent>
              <MeetingDiscussionDialog
                handleClose={handleMeetingDiscussionClose}
                saveForm={handleAddMeetingDiscussionItem}
                discussionNotes={discussionNotes}
                setDiscussionNotes={setDiscussionNotes}
                auth={auth}
                meetingDiscussionItemSubmittedAttempted={meetingDiscussionItemSubmittedAttempted}
                setMeetingDiscussionItemSubmittedAttempted={setMeetingDiscussionItemSubmittedAttempted}
              />
            </DialogContent>
          </Dialog>
        )}

        {isAdminOrFHT(auth.roles) && (
          <>
            <summary style={{ marginBottom: '30px', textAlign: 'left', width: '100%', padding: '10px', border: '2px solid ' + btNavyBlueColor }}>
              <section align='left' className={formClasses.meetingDiscussionTitle}>
                <span style={{ width: '75%' }}>
                  Specialists Notes: Items to address during the planning meeting{' '}
                  {Array.isArray(discussionNoteUpdates) && discussionNoteUpdates.length > 0 && (
                    <Tooltip
                      title={
                        discussionNoteUpdates
                          ? typeof discussionNoteUpdates === 'object'
                            ? formatDiscussionUpdateData(discussionNoteUpdates)
                            : ''
                          : ''
                      }
                      placement='top'
                      classes={{ tooltip: formClasses.discussionTooltip }}
                    >
                      <InfoIcon
                        style={{ color: btNavyBlueColor, marginLeft: '5px', verticalAlign: 'middle', cursor: 'pointer' }}
                        className={formClasses.unprintedContent}
                      />
                    </Tooltip>
                  )}
                </span>
                <span style={{ width: '40%', textAlign: 'right' }} className={formClasses.unprintedContent}>
                  <Button
                    className={formClasses.meetingDiscussionButton}
                    color='primary'
                    variant='contained'
                    onClick={() => editDiscussionNotes(true)}
                    disabled={!isAdmin(auth.roles)}
                    style={{ margin: '0 0 7px 0' }}
                  >
                    Edit Notes
                  </Button>
                </span>
              </section>

              <div className={formClasses.meetingDiscussionNotes}>{discussionNotes || '(No discussion notes yet)'}</div>
            </summary>
          </>
        )}

        {store.senior && (
          <summary>
            <section align='left' className={formClasses.unprintedContent} style={{ width: '95%', textAlign: 'center', marginTop: '15px' }}>
              NOTE: This Planning Form - Meeting #2 will be automatically saved every five minutes.
              <br />
              <br />
            </section>
            {form.status && form.status.length > 0 && (
              <>
                <Grid container align='left' className={formClasses.unprintedContent}>
                  <Grid item xs={12} align='center' className={formClasses.textRowLabelSpacer} style={{ minHeight: 30 }}>
                    <aside style={{ fontWeight: 600, fontSize: '1.25rem' }}>Current Status: {displayStatus(form.status).toUpperCase()}</aside>
                  </Grid>
                </Grid>

                <div className={formClasses.printedContent}>
                  <aside style={{ fontWeight: 600, fontSize: '1.50rem', paddingBottom: 10 }} align='center'>
                    Current Status: {displayStatus(form.status).toUpperCase()}
                  </aside>
                </div>
              </>
            )}
            {(form.status === 'approved' || form.status === 'incomplete' || showIncomplete) && (
              <aside className={formClasses.totalScore} align='center'>
                Planning Form Score:{' '}
                {(form.gradingSheet2Completed || 0) +
                  (form.gradingSheet2Submitted || 0) +
                  (form.gradingSheet2Clarity || 0) +
                  (form.module4Submitted || 0) +
                  (form.module4Completed || 0) +
                  (form.module5Submitted || 0) +
                  (form.module5Completed || 0) +
                  (form.module6Submitted || 0) +
                  (form.module6Completed || 0) >
                0
                  ? (form.gradingSheet2Completed > -1 ? form.gradingSheet2Completed : 0) +
                    (form.gradingSheet2Submitted > -1 ? form.gradingSheet2Submitted : 0) +
                    (form.gradingSheet2Clarity > -1 ? form.gradingSheet2Clarity : 0) +
                    (form.module4Submitted > -1 ? form.module4Submitted : 0) +
                    (form.module4Completed > -1 ? form.module4Completed : 0) +
                    (form.module5Submitted > -1 ? form.module5Submitted : 0) +
                    (form.module5Completed > -1 ? form.module5Completed : 0) +
                    (form.module6Submitted > -1 ? form.module6Submitted : 0) +
                    (form.module6Completed > -1 ? form.module6Completed : 0)
                  : 0}
              </aside>
            )}
          </summary>
        )}

        {shouldDisplayStatusNotes() && (
          <Grid item xs={12} align='left' className={formClasses.dispositionNote + ' ' + formClasses.space10After}>
            {form.dispositionReason && form.dispositionReason.length > 0 && (
              <React.Fragment>
                <summary style={{ fontWeight: 600 }}>Notes Regarding Status</summary>
                <summary>{form.dispositionReason}</summary>
              </React.Fragment>
            )}
          </Grid>
        )}

        {store.senior && (
          <Grid container key={store.senior.userId} align='left' style={{ marginBottom: '75px' }}>
            <Grid container direction='row'>
              <Grid item xs={12} align='left' className={formClasses.firstSectionSpacer}>
                <section align='left' className={formClasses.firstSectionTitle}>
                  General Information
                </section>
              </Grid>
            </Grid>
            {store.senior &&
              (form.version === PLANNING_MEETING_FORM_VERSION ? (
                <GeneralInformationV2 senior={store.senior} form={form} setForm={setForm} disabled={isEditDisabled()} />
              ) : (
                <GeneralInformation senior={store.senior} />
              ))}
            {form?.version !== PLANNING_MEETING_FORM_VERSION && (
              <>
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Spiritual/Religious Information
                    </section>
                  </Grid>
                </Grid>
                <SpiritualInformation className={formClasses.topMargin10} form={form} setForm={setForm} disabled={isEditDisabled()} />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left' className={formClasses.hideForMd}>
                    <section align='left' className={formClasses.sectionTitle}>
                      Health Insurance Plan
                    </section>
                  </Grid>
                  <Grid item xs={12} align='left' className={formClasses.hideForXs}>
                    <section align='left' className={formClasses.sectionTitle}>
                      Post-Graduation Health Insurance Plan
                    </section>
                  </Grid>
                </Grid>
                <HealthInsurancePlan form={form} setForm={setForm} disabled={isEditDisabled()} saveHealthInsuranceTask={saveHealthInsuranceTask} />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Support and Activities for Post-Graduation
                    </section>
                  </Grid>
                </Grid>
                <Box sx={{ width: '90%' }}>
                  <Grid container direction='row'>
                    <Grid item xs={12} md={5} lg={5} className={formClasses.unprintedContent}>
                      <section align='left' className={formClasses.bigLabel}>
                        These are people I can contact post-graduation for guidance and support.{' '}
                      </section>
                      <ExpandingList
                        className={formClasses.unprintedContent}
                        form={form}
                        setForm={setForm}
                        listname='support'
                        field1='name'
                        field1Cols={11}
                        field1MaxLength={50}
                        rowsRequired={1}
                        disabled={isEditDisabled()}
                      />
                    </Grid>
                    <Grid item xs={false} md={1} lg={1}></Grid>
                    <Grid item xs={12} md={6} lg={6} className={formClasses.unprintedContent}>
                      <section align='left' className={formClasses.bigLabel}>
                        Current positive activities I participate in and will continue to participate in after graduation.{' '}
                      </section>
                      <ExpandingList
                        className={formClasses.unprintedContent}
                        form={form}
                        setForm={setForm}
                        listname='activities'
                        field1='name'
                        field1Cols={11}
                        field1MaxLength={50}
                        rowsRequired={1}
                        disabled={isEditDisabled()}
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction='row' className={formClasses.printedContent}>
                    <Grid item xs={12}>
                      <label>These are people I can contact post-graduation for guidance and support.</label>
                    </Grid>
                    <Grid item xs={12}>
                      <summary className={formClasses.alignPrintedContent}>{createPrintedList(form.support)}</summary>
                    </Grid>
                  </Grid>
                  <Grid container direction='row' className={formClasses.printedContent}>
                    <Grid item xs={12}>
                      <label>Current positive activities I participate in and will continue to participate in after graduation.</label>
                    </Grid>
                    <Grid item xs={12}>
                      <summary className={formClasses.alignPrintedContent}>{createPrintedList(form.activities)}</summary>
                    </Grid>
                  </Grid>
                </Box>

                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Transportation Plan
                    </section>
                  </Grid>
                </Grid>
                <TransportationPlan
                  form={form}
                  setForm={setForm}
                  disabled={isEditDisabled()}
                  saveDriversLicenseTask={saveDriversLicenseTask}
                  saveDriversEdTask={saveDriversEdTask}
                  saveLearnersPermitTask={saveLearnersPermitTask}
                  saveAutoInsuranceTask={saveAutoInsuranceTask}
                  handleHaveVehicle={handleHaveVehicle}
                />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Summer Plans
                    </section>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionSubTitle}>
                      Summer Housing Plan
                    </section>
                  </Grid>
                </Grid>
                <HousingPlan
                  form={form}
                  setForm={setForm}
                  disabled={isEditDisabled()}
                  housingPlanText={'When I depart on the day of graduation, I will go to:'}
                  label={'What responsibilities will I have living here?'}
                />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionSubTitle}>
                      Summer Employment Plan
                    </section>
                  </Grid>
                </Grid>
                <EmploymentPlan form={form} setForm={setForm} disabled={isEditDisabled()} saveTalkedToManagerTask={saveTalkedToManagerTask} />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Fall Plans
                    </section>
                  </Grid>
                </Grid>
                <FallPlan
                  form={form}
                  setForm={setForm}
                  act={store.senior && store.senior.act}
                  disabled={isEditDisabled()}
                  saveJobCorpsStartDateTask={saveJobCorpsStartDateTask}
                  saveMilitaryMEPSDateTask={saveMilitaryMEPSDateTask}
                  saveMilitaryBasicTrainingDateTask={saveMilitaryBasicTrainingDateTask}
                  saveFallPlanAndTasks={saveFallPlanAndTasks}
                />
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Back-up Plan
                    </section>
                  </Grid>
                </Grid>
                <BackupPlan form={form} setForm={setForm} disabled={isEditDisabled()} />

                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.bigLabel} style={{ marginTop: '20px' }}>
                    <section className={formClasses.tasksHeader}>Tasks I need to complete to move toward my plan. </section>
                  </Grid>

                  {form.automaticTasks && form.automaticTasks.length > 0 && (
                    <Grid item xs={12} className={formClasses.bigLabel + ' ' + formClasses.automaticTaskHeading}>
                      <label>Based on your responses, consider the following items when coming up with tasks:</label>
                    </Grid>
                  )}
                </Grid>

                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.tasks}>
                    <ul>
                      {form.automaticTasks &&
                        form.automaticTasks.length > 0 &&
                        form.automaticTasks.map((t, idx) => {
                          return (
                            <li key={'autotasks' + idx} className={formClasses.automaticTasksList}>
                              {t}
                            </li>
                          );
                        })}
                    </ul>
                  </Grid>
                </Grid>

                <Grid container direction='row' style={{ height: '185px', overflow: 'auto' }} className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.textRowSpacer}>
                    <ExpandingList
                      form={form}
                      setForm={setForm}
                      listname='tasks'
                      field1='name'
                      field1Cols={11}
                      field1MaxLength={500}
                      rowsRequired={5}
                      disabled={isEditDisabled()}
                    />
                  </Grid>
                </Grid>
                <div className={formClasses.printedContent}>
                  <div>
                    <label>Tasks I need to complete to move toward my plan.</label>
                  </div>
                  <div>
                    <ul>
                      {form.tasks &&
                        form.tasks.length > 0 &&
                        form.tasks.map((t, idx) => {
                          return (
                            <li key={'printedtasks_' + idx} className={formClasses.printedTasksList}>
                              {t.name}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                  <br />
                </div>

                <Grid container direction='row' className={formClasses.sectionSpacer + ' ' + formClasses.unprintedContent}>
                  <Grid item xs={11} align='left'>
                    <section align='left' className={formClasses.sectionSubTitle + ' ' + formClasses.reminder}>
                      PLEASE READ THE FOLLOWING STATEMENTS AND INITIAL IN THE SPACE PROVIDED:
                    </section>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={11} sm={12} md={9} lg={8} className={formClasses.textRowLabelSpacer}>
                    <section>I understand I can access the Boys Town scholarship application online and am aware of the due date.</section>
                  </Grid>
                  <Grid item xs={3} sm={3} md={1} lg={1} className={formClasses.textRowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='scholarship-received-initial'
                        size='small'
                        hiddenLabel
                        placeholder='Initials'
                        variant='outlined'
                        value={form.scholarshipReceivedInitial}
                        onChange={(event) => setForm({ ...form, scholarshipReceivedInitial: event.target.value })}
                        InputProps={{ classes: { input: formClasses.textField } }}
                        inputProps={{ maxLength: 5 }}
                        disabled={isEditDisabled()}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.printedContent}>
                  <Grid item xs={8}>
                    <label className={formClasses.alignPrintedContent}>
                      I understand I can access the Boys Town scholarship application online and am aware of the due date.
                    </label>
                  </Grid>
                  <Grid item xs={1}>
                    <summary className={formClasses.alignPrintedContent}>{form.scholarshipReceivedInitial || null}</summary>
                  </Grid>
                </Grid>

                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={11} sm={12} md={9} lg={8} className={formClasses.textRowLabelSpacer}>
                    <section>I understand what I need to do for Boys Town to be able to support my plan.</section>
                  </Grid>
                  <Grid item xs={3} sm={3} md={1} lg={1} className={formClasses.textRowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='understand-what-i-need-to-do-initial'
                        size='small'
                        hiddenLabel
                        variant='outlined'
                        placeholder='Initials'
                        value={form.understandWhatINeedToDoInitial}
                        onChange={(event) => setForm({ ...form, understandWhatINeedToDoInitial: event.target.value })}
                        InputProps={{ maxLength: 5, classes: { input: formClasses.textField } }}
                        disabled={isEditDisabled()}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.printedContent}>
                  <Grid item xs={8}>
                    <label className={formClasses.alignPrintedContent}>
                      I understand what I need to do for Boys Town to be able to support my plan.
                    </label>
                  </Grid>
                  <Grid item xs={1}>
                    <summary className={formClasses.alignPrintedContent}>{form.understandWhatINeedToDoInitial || null}</summary>
                  </Grid>
                </Grid>
              </>
            )}
            {form.version === PLANNING_MEETING_FORM_VERSION && (
              <MyFuturePlan
                form={form}
                setForm={setForm}
                auth={auth}
                classes={formClasses}
                act={store.senior && store.senior.act}
                saveTalkedToManagerTask={saveTalkedToManagerTask}
                disabled={isEditDisabled()}
                saveDriversLicenseTask={saveDriversLicenseTask}
                saveLearnersPermitTask={saveLearnersPermitTask}
                handleHaveVehicle={handleHaveVehicle}
                saveAutoInsuranceTask={saveAutoInsuranceTask}
                saveDriversEdTask={saveDriversEdTask}
                saveHealthInsuranceTask={saveHealthInsuranceTask}
                saveMilitaryMEPSDateTask={saveMilitaryMEPSDateTask}
                saveMilitaryBasicTrainingDateTask={saveMilitaryBasicTrainingDateTask}
              />
            )}

            {/* Action Buttons */}
            <Grid container direction='row' className={formClasses.unprintedContent}>
              <Grid item xs={12} sm={12} md={3} align='right' className={formClasses.buttonSpacer + ' ' + formClasses.unprintedContent}>
                <div align='left'>
                  {(!form.status || form.status === 'created' || form.status === 'reopened' || form.status === 'incomplete' || showIncomplete) &&
                    (isStudent(auth.roles) || isAlumni(auth.roles)) && (
                      <Tooltip title='Click to submit your planning form 2 for review.' placement='top'>
                        <Button
                          className={formClasses.floatingSubmitButton}
                          color='secondary'
                          variant='contained'
                          onClick={() => setShowConfirmSubmitDialog(true)}
                          disabled={!isStudent(auth.roles) && !isAlumni(auth.roles)}
                          style={{ margin: '0 15px 15px 0', width: '110px' }}
                        >
                          Submit
                        </Button>
                      </Tooltip>
                    )}
                </div>
                <div align='right'>
                  {showSaveButton() ? (
                    <>
                      <Button
                        className={formClasses.floatingSaveButton}
                        variant='contained'
                        onClick={() => handleSaveReturn()}
                        disabled={!store.senior}
                        style={{ margin: '0 140px 15px 0', width: '110px' }}
                      >
                        {isFHT(auth.roles) ? 'Close' : 'Cancel'}
                      </Button>
                      <Button
                        className={formClasses.floatingSaveButton}
                        color='primary'
                        variant='contained'
                        onClick={() => handleSavePlanningForm2(true)}
                        disabled={!store.senior || isFHT(auth.roles)}
                        style={{ margin: '0 15px 15px 0', width: '110px' }}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      className={formClasses.floatingSaveButton}
                      variant='contained'
                      onClick={() => handleSaveReturn()}
                      disabled={!store.senior}
                      style={{ margin: '0 15px 15px 0', width: '110px' }}
                    >
                      {isFHT(auth.roles) ? 'Close' : 'Cancel'}
                    </Button>
                  )}
                </div>
              </Grid>
            </Grid>

            {/* Submitted Date */}
            {form.status === 'submitted' && form.submitted_date && (
              <Grid container direction='row' className={formClasses.sectionSpacer} style={{ marginTop: '30px' }}>
                <Grid item xs={12} align='left'>
                  <summary className={formClasses.submittedTS}>Planning Form Submitted: {dateTimeToS(form.submitted_date)}</summary>
                </Grid>
              </Grid>
            )}

            {/* Meeting Notes */}
            {!isStudent(auth.roles) && !isAlumni(auth.roles) && !isFHT(auth.roles) && (
              <React.Fragment>
                <Grid container direction='row' className={formClasses.sectionSpacer}>
                  <Grid item xs={12} sm={12} md={6} align='left'>
                    <section align='left' className={formClasses.sectionTitle}>
                      Meeting Notes
                    </section>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.textArea5RowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='meetingComment'
                        ref={meetingCommentRef}
                        size='small'
                        hiddenLabel
                        variant='outlined'
                        value={addMeetingNote}
                        onChange={(event) => setAddMeetingNote(event.target.value)}
                        disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                        InputProps={{
                          classes: { input: formClasses.textField },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} className={formClasses.textArea5RowSpacer}>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={() => handleAddMeetingComment()}
                      disabled={!addMeetingNote}
                      style={{ margin: '0 0 0 15px' }}
                    >
                      Add Note
                    </Button>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.textRowSpacer}>
                    <aside className={formClasses.meetingSaveNote}>
                      NOTE: Meeting notes are not visible to students. After adding a note you MUST click "SAVE" to save the Planning Form.
                    </aside>
                  </Grid>
                </Grid>
                <summary
                  className={formClasses.unprintedContent}
                  style={{ height: '300px', width: '90%', border: '1px solid #808080', overflowY: 'scroll' }}
                >
                  <Grid container direction='row' className={formClasses.sectionSpacer}>
                    <Grid item xs={12} sm={12} md={9}>
                      {form.meetingComments &&
                        form.meetingComments
                          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                          .map((comment, idx) => {
                            return (
                              <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                                <tr style={{ margin: '5px 0' }}>
                                  <td style={{ width: '100%', verticalAlign: 'top' }}>
                                    <aside>
                                      <label>Logged at</label> {comment.timestamp}
                                    </aside>
                                    <aside>
                                      <label>By</label> {comment.user}
                                    </aside>
                                    <summary>{comment.text}</summary>
                                  </td>
                                </tr>
                              </table>
                            );
                          })}
                    </Grid>
                  </Grid>
                </summary>
                <summary className={formClasses.printedContent} style={{ width: '90%' }}>
                  {form.meetingComments &&
                    form.meetingComments
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((comment, idx) => {
                        return (
                          <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                            <tr style={{ margin: '5px 0' }}>
                              <td style={{ width: '100%', verticalAlign: 'top' }}>
                                <aside>
                                  <label>Logged at</label> {comment.timestamp}
                                </aside>
                                <aside>
                                  <label>By</label> {comment.user}
                                </aside>
                                <summary>{comment.text}</summary>
                              </td>
                            </tr>
                          </table>
                        );
                      })}
                </summary>
              </React.Fragment>
            )}

            {/* Comments */}
            <Grid container direction='row' className={formClasses.sectionSpacer}>
              <Grid item xs={12} sm={12} md={6} align='left'>
                {isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles) ? (
                  <section align='left' className={formClasses.disabledSectionTitle}>
                    Comments
                  </section>
                ) : (
                  <section align='left' className={formClasses.sectionTitle}>
                    Comments
                  </section>
                )}
              </Grid>
            </Grid>
            {!isStudent(auth.roles) && !isAlumni(auth.roles) && !isFHT(auth.roles) && (
              <React.Fragment>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.textArea5RowSpacer}>
                    <FormControl fullWidth>
                      <TextField
                        id='comment'
                        ref={commentRef}
                        size='small'
                        hiddenLabel
                        variant='outlined'
                        value={addNote}
                        onChange={(event) => setAddNote(event.target.value)}
                        disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                        InputProps={{
                          classes: { input: formClasses.textField },
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} lg={2} className={formClasses.textArea5RowSpacer}>
                    <Button
                      color='primary'
                      variant='contained'
                      onClick={() => handleAddComment()}
                      disabled={!addNote}
                      style={{ margin: '0 0 0 15px' }}
                    >
                      Add Comment
                    </Button>
                  </Grid>
                </Grid>
                <Grid container direction='row' className={formClasses.unprintedContent}>
                  <Grid item xs={12} className={formClasses.textRowSpacer}>
                    <aside className={formClasses.commentSaveNote}>
                      NOTE: Comments should be used to advise students. They will be seen by the student. After adding a comment you MUST click "SAVE"
                      to save the Planning Form.
                    </aside>
                  </Grid>
                </Grid>
              </React.Fragment>
            )}

            <summary
              className={formClasses.unprintedContent}
              style={{ height: '300px', width: '90%', border: '1px solid #808080', overflowY: 'scroll', marginBottom: 35 }}
            >
              <Grid container direction='row' className={formClasses.sectionSpacer}>
                <Grid item xs={12} sm={12} md={9}>
                  {form.comments &&
                    form.comments
                      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                      .map((comment, idx) => {
                        return (
                          <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                            <tr style={{ margin: '5px 0' }}>
                              <td style={{ width: '100%', verticalAlign: 'top' }}>
                                <aside>
                                  <label>Logged at</label> {comment.timestamp}
                                </aside>
                                <aside>
                                  <label>By</label> {comment.user}
                                </aside>
                                <summary>{comment.text}</summary>
                              </td>
                            </tr>
                          </table>
                        );
                      })}
                </Grid>
              </Grid>
            </summary>
            <summary className={formClasses.printedContent} style={{ width: '90%', marginBottom: 35 }}>
              {form.comments &&
                form.comments
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((comment, idx) => {
                    return (
                      <table style={{ width: '100%', marginBottom: '20px', fontSize: '0.90rem' }} key={idx}>
                        <tr style={{ margin: '5px 0' }}>
                          <td style={{ width: '100%', verticalAlign: 'top' }}>
                            <aside>
                              <label>Logged at</label> {comment.timestamp}
                            </aside>
                            <aside>
                              <label>By</label> {comment.user}
                            </aside>
                            <summary>{comment.text}</summary>
                          </td>
                        </tr>
                      </table>
                    );
                  })}
            </summary>
          </Grid>
        )}

        {!isStudent(auth.roles) && !isAlumni(auth.roles) && !isFHT(auth.roles) && (
          <Grid container direction='row' className={formClasses.unprintedContent}>
            <Grid item xs={12} align='left'>
              <FormControlLabel
                style={{ marginLeft: '10px' }}
                control={
                  <Tooltip
                    title='Student has not submitted the planning form, but we need to score the other pieces required for incentive payments.'
                    placement='top'
                  >
                    <Checkbox
                      color='default'
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      checked={showIncomplete}
                      onChange={() => {
                        markFormIncomplete(!showIncomplete);
                      }}
                    />
                  </Tooltip>
                }
                label='Show Admin Form for Incomplete Grading'
              />
            </Grid>
          </Grid>
        )}

        {/* Administrative Disposition Panel */}
        {showDisposition() && (
          <Grid
            container
            direction='row'
            className={formClasses.unprintedContent}
            style={{ marginTop: 20, padding: 20, border: '1px solid darkgreen', background: '#dcf5dc' }}
          >
            <Grid item xs={12} align='center'>
              <label style={{ fontWeight: 600 }}>Successful Futures Administration Only</label>
            </Grid>
            <Grid item xs={12} sm={5} md={3} lg={2} className={formClasses.textRowLabelSpacer} align='left'>
              <label style={{ fontWeight: 600, color: '#000000' }}>Planning Form 2 Status</label>
            </Grid>
            <Grid item xs={12} sm={12} md={5} lg={6} className={formClasses.textArea5RowSpacer}>
              <FormControl fullWidth>
                <RadioGroup
                  row
                  aria-label='approve planning form 2'
                  name='approve-planning-form-2'
                  horizontal='true'
                  value={form.status}
                  onChange={(event) => setForm({ ...form, status: event.target.value, statusTimestamp: new Date() })}
                  disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                >
                  {!showIncomplete && (
                    <FormControlLabel
                      value='reopened'
                      className={formClasses.marginRight40}
                      control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                          Re-Opened
                        </span>
                      }
                    />
                  )}

                  {!showIncomplete && (
                    <FormControlLabel
                      value='approved'
                      className={formClasses.marginRight40}
                      control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                          Approved
                        </span>
                      }
                    />
                  )}

                  <FormControlLabel
                    value='incomplete'
                    className={formClasses.marginRight40}
                    control={<Radio color='default' disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />}
                    label={
                      <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                        Incomplete
                      </span>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4} className={formClasses.textArea5RowSpacer} align='right'>
              <FormControlLabel
                style={{ marginLeft: '10px' }}
                control={
                  <Tooltip title='Student needs an additional senior planning meeting to address shortcomings in their current plan.' placement='top'>
                    <Checkbox
                      color='default'
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      checked={form.followup ? form.followup : false}
                      onChange={(ev) => {
                        if (ev.target.checked) {
                          setForm({ ...form, followup: true });
                        } else {
                          setForm({ ...form, followup: ev.target.checked });
                        }
                      }}
                    />
                  </Tooltip>
                }
                label='Needs Follow-up'
              />
            </Grid>

            {(form.status === 'reopened' || form.status === 'approved') && (
              <React.Fragment>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id='disposition-reason'
                      size='small'
                      fullWidth
                      label={`Enter any notes for status '${titleize(form.status)}'`}
                      variant='outlined'
                      value={form.dispositionReason}
                      onChange={(event) => setForm({ ...form, dispositionReason: event.target.value })}
                      disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)}
                      InputProps={{ classes: { input: formClasses.textField } }}
                      inputProps={{ maxLength: 500 }}
                      className={formClasses.root}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <label className={formClasses.note}>
                    {form.status === 'reopened'
                      ? 'This reason will be displayed to the student when they view the reopened planning form.'
                      : 'This reason will be displayed to the student when they view the planning form.'}
                  </label>
                </Grid>
              </React.Fragment>
            )}

            <Scoring form={form} setForm={setForm} disabled={isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles)} />
          </Grid>
        )}

        <RequiredInfoDialog
          title='Planning Form 2 Errors'
          introduction={requiredFieldsIntro}
          content={requiredFields}
          showDialog={showRequiredFieldsDialog}
          handleClose={handleRequiredInfoClose}
        />
        <ConfirmSubmitDialog
          handleContinueSubmit={handleContinueSubmit}
          setShowConfirmSubmitDialog={setShowConfirmSubmitDialog}
          showDialog={showConfirmSubmitDialog}
          handleClose={handleConfirmSubmitDialogClose}
        />
        <Snackbar
          ContentProps={{
            classes: {
              root: formClasses.warning,
            },
          }}
          open={showWarning}
          autoHideDuration={6000}
          onClose={handleClose}
          message={warning}
          action={action}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        ></Snackbar>
        <Snackbar
          ContentProps={{
            classes: {
              root: formClasses.errorAlert,
            },
          }}
          open={showError}
          autoHideDuration={10000}
          onClose={handleErrorClose}
          message={error}
          action={errorAction}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ></Snackbar>
      </React.Fragment>
    );
  })
);

SeniorPlanning2.propTypes = {
  handleSaveReturn: PropTypes.func,
};

export default SeniorPlanning2;
